/**
 * The numbered pins: the coach's invitation, written on the graph itself.
 *
 * The card explains one cue. The pins say how many there are and WHERE they are,
 * which is the question a person actually has when a run stops — so they render
 * whether or not the card is open. Opening the card is what a pin is FOR.
 *
 * The discipline is `Labels.tsx`'s, exactly, and for the same reason: a pin is a
 * DOM element so it can be a real button with a real accessible name, and the
 * cost of that is that its position has to be pushed to the DOM every frame. So
 * the elements are rendered once per (visible cue list, current index, visible
 * node set) change, their refs are held in a Map, and one `onAfterDraw`
 * subscription writes `style.transform` from `screen.place(anchorId)` — never a
 * React render during a pan. `screen.place` is only valid inside that callback;
 * anywhere else it is last frame's camera.
 *
 * The pins are NOT registered in `Labels.tsx`'s chrome-avoidance list. That list
 * holds fixed chrome — panels whose rectangles a caption must never be placed
 * under — and a pin is a node-anchored mover: registering one would exile a band
 * of captions that travels with the graph. Instead a pin sits up and to the right
 * of its node, the sheet's own placement, and the residual overlap with that
 * node's own caption is accepted: both belong to the same node and both are
 * small.
 *
 * It still has to READ that chrome, though, which is the other half of the same
 * fact. A pin under the composer is painted where nobody can see it and clicked
 * by nobody either — the panel above takes the press — so it is an accessible
 * control that is neither visible nor reachable. Every rect in `CHROME` is
 * measured per frame and a pin whose box lands inside one is hidden, which drops
 * it from the tab order with it. Nothing is lost: J, K and the digest reach that
 * cue without a pointer, and a number pointing at a place you cannot look is
 * worse than no number.
 */

import { useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { onAfterDraw } from '../canvas/frameBus'
import { screen } from '../canvas/screen'
import { useGraphFrame } from '../canvas/useGraphFrame'
import { CUE_LABEL, type CueKind } from '../core/cues'
import { useStore } from '../core/store'
import type { Channel } from '../core/types'
import { useLiveCoach, usePinsShow, useVisibleCues } from './coachView'

/**
 * Up and to the right of the node — the sheet's placement, kept.
 *
 * Far enough to clear the node glyph, near enough that the pin still reads as
 * belonging to it rather than floating in the field.
 */
const PIN_DX = 18
const PIN_DY = -18

/** The secondary dot rides the same diagonal, closer in. It is a smaller mark
 *  for a smaller claim: this node is MENTIONED by the cue, not what it is about. */
const DOT_DX = 12
const DOT_DY = -12

/** Half-boxes, for the on-screen and under-chrome tests. The pin is 19px and the
 *  dot 7px (`.cuepin`, `.cuepin--also`); a couple of pixels of slack keeps a mark
 *  from touching an edge it is technically clear of. */
const PIN_R = 11
const DOT_R = 5

/**
 * The fixed chrome a pin must not hide behind.
 *
 * The same rectangles `Labels.tsx` keeps captions out from under, plus the
 * composer's own column — which the captions can afford to overlap and a
 * CONTROL cannot, because the composer's glass takes the press. Measured every
 * frame and never assumed at a corner: all of it moves with the floating panes'
 * insets, and some of it is not drawn at all on a narrow stage. No element, no
 * rect.
 *
 * The thread pane is in this list too, and by the name it carries rather than by
 * a class: its shell is `GlassPane`, which takes no `className`, and the
 * `aria-label` is `Thread.tsx`'s own — the coupling is named at both ends. It
 * was once handled by an inset gate below and by nothing else, which held on a
 * wide stage and failed exactly where it hurt: on a phone the pane is a BOTTOM
 * SHEET, App holds the inset at 0 by construction, and a numbered pin under the
 * open sheet stayed drawn, focusable and unpressable — the sheet's own tab strip
 * took the press, which is the precise condition the note above forbids. A
 * measured rectangle finds the pane in both layouts, so the rule no longer
 * depends on which shape it is wearing.
 *
 * The coach's own CARD is the last entry, and the only MOVER in the list — it is
 * the popup this layer's cues open, floating beside a node at `--z-float`, above
 * these pins and taking presses the moment it is placed (`.coach--popup`). It is
 * bigger chrome than the run-done bar, which is here for the same reason: two
 * cues on nearby nodes put pin 2 under the card that cue 1 opened, and that pin
 * was drawn, focusable and unpressable behind the card's glass. A mover is fine
 * HERE, unlike in `Labels.tsx`'s reserved-rectangle list, precisely because this
 * is a per-frame `querySelector` pass: the rect is whatever the card's last
 * placed frame produced rather than a region reserved for all time. The seat's
 * `[data-placed]` is part of the selector on purpose — an unplaced seat is still
 * parked in the stage's corner and takes no presses, so its rectangle blocks
 * nothing.
 *
 * The cue being READ keeps its own pin through this: the card stands a full
 * `CARD_GAP` off its anchor and a pin sits 18px from it, so the card's box
 * begins past its own number. What hides is the other cues' pins, and J, K and
 * the digest reach those without a pointer — the trade the note above already
 * made for every other pane.
 */
const CHROME = [
  '.slot-session',
  '.slot-composer',
  '.minimap',
  // The graph footer (D369), which carries the transport and the scrubber.
  '.graphfoot',
  '.tooldock',
  // The coach's quiet line and its ✕, at the head of the run-done column where
  // the bar stood (`CoachLine` in `ui/RunDoneBar.tsx`).
  '.coachline',
  '.coachline-close',
  // …and the card's phone form, the last row of that column: docked, the
  // field's width and half its height, so a pin under it was drawn through its
  // glass and could not be pressed. Fixed, like the line, so `Labels.tsx`
  // reserves it too.
  '.coach--column',
  // COUPLED to `ariaLabel="Thread"` in `ui/Thread.tsx`, and named there too: the
  // shell is `GlassPane`, which takes no `className`, so the label is the one
  // handle a selector has. Rename one and the other must follow in the same
  // change, or pins under the pane's glass go back to drawn-but-unpressable.
  'aside[aria-label="Thread"]',
  // The explorations sidebar, by its label for the inspector's reason — the same
  // `GlassPane` shell, which takes no `className`. Both of its shapes are
  // caught by one selector: the desktop column, and the phone overlay that the
  // `viewInset.left` gate below cannot see because App holds that inset at 0
  // there. That is the inspector's bottom-sheet finding, on the other side.
  'aside[aria-label="Explorations"]',
  '.coachpop__seat[data-placed]',
] as const

interface Pin {
  /** Ref key. A cue key for a numbered pin, `also:<nodeId>` for a dot. */
  id: string
  anchorId: string
  /** 1-based position in `visibleCues`, or null for a secondary dot. */
  n: number | null
  /** The cue's index, for the press. Meaningless on a dot, which is inert. */
  i: number
  kind: CueKind
  channel: Channel
  /** The node's own label, for a dot's tooltip. Null on a numbered pin, whose
   *  name is its cue's. */
  name: string | null
  dx: number
  dy: number
}

export function CoachPins() {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const refs = useRef(new Map<string, HTMLElement>())
  // Through `liveCoach`, like every other coach surface: a number on this graph
  // must never come from another session's reading of a different one.
  const coach = useLiveCoach()
  const openCoach = useStore((s) => s.openCoach)
  const setCoachIndex = useStore((s) => s.setCoachIndex)
  const cues = useVisibleCues()
  const { session, visible } = useGraphFrame()

  // The card's clamp, once more. The pin the card is showing is the emphasized
  // one, and two readings of "which cue" is how they would come to disagree.
  const current = cues.length > 0 ? Math.min(Math.max(0, coach?.index ?? 0), cues.length - 1) : -1

  // One predicate, shared with the test that pins it (`coachView.ts`). Not gated
  // on the CARD — the pins are the invitation, and an invitation that only
  // appears once you have accepted it is no invitation at all — but gated on the
  // coach having a surface left at all, so Escape's last rung leaves the graph
  // clean instead of leaving numbers with nothing to explain them.
  const show = usePinsShow()

  const pins = useMemo((): Pin[] => {
    const out: Pin[] = []
    if (!show) return out

    // A numbered pin claims its node. `assembleCues` already dedupes per anchor
    // — one cue per node, the highest-ranked — so this set is exactly the cue
    // list's anchors and no node ever carries two numbers.
    const claimed = new Set(cues.map((c) => c.anchorId))

    cues.forEach((cue, i) => {
      // Numbering follows `visibleCues`, not the pins that survive this filter:
      // a cue whose node is behind a fold or a display switch simply has no pin,
      // and the gap in the numbers is the honest report of that. Renumbering
      // would make the card's "3 of 8" name a different cue than pin 3.
      if (!visible.has(cue.anchorId)) return
      out.push({
        id: cue.key,
        anchorId: cue.anchorId,
        n: i + 1,
        i,
        kind: cue.kind,
        channel: cue.channel,
        name: null,
        dx: PIN_DX,
        dy: PIN_DY,
      })
    })

    // The nodes a cue MENTIONS — the objection, the newer record, the far end of
    // a join. Never numbered: a number is a position in the reading order and a
    // secondary node has none. First cue in the order wins a node that two
    // mention, and a node already carrying a number keeps it — the number says
    // strictly more than the dot does.
    const dotted = new Set<string>()
    for (const cue of cues) {
      for (const id of cue.alsoIds) {
        if (claimed.has(id) || dotted.has(id) || !visible.has(id)) continue
        const node = session.nodes[id]
        if (!node) continue
        dotted.add(id)
        out.push({
          id: `also:${id}`,
          anchorId: id,
          n: null,
          i: -1,
          kind: cue.kind,
          channel: cue.channel,
          name: node.label,
          dx: DOT_DX,
          dy: DOT_DY,
        })
      }
    }
    return out
  }, [show, cues, visible, session])

  useEffect(() => {
    if (!show) return

    const tick = () => {
      const host = hostRef.current
      if (!host || pins.length === 0) return
      const W = host.clientWidth
      const H = host.clientHeight

      // Read per frame and not subscribed, exactly as the caption layer reads
      // its own: this layer never re-renders during a pan, so a panel opening
      // has to change the answer on the very next frame.
      const hr = host.getBoundingClientRect()
      // The panes' own boxes are measured through `CHROME` above. THIS is the
      // wide stage's extra, and it is not the same reading: `viewInset` is the
      // columns the whole app agrees the panes take — App sets them from the
      // rail state, the camera centres against them — and they run wider than
      // the glass itself, so a pin in the gutter beside a pane is still a pin
      // in a column the reader's own camera is being kept out of. Read
      // imperatively in the tick that reads the camera, exactly as the chrome
      // above is, so a pane opening changes the answer on the very next frame
      // rather than on the next React render.
      //
      // On a phone both are 0 by construction (the thread is a bottom sheet,
      // the sidebar an overlay), so these arms do not fire there and the panes'
      // measured rectangles are what cover that case — see `CHROME`.
      const { left: insetLeft, right: insetRight } = useStore.getState().viewInset
      const blocks: Array<{ l: number; t: number; r: number; b: number }> = []
      for (const sel of CHROME) {
        const el = document.querySelector(sel)
        if (!el) continue
        const br = (el as HTMLElement).getBoundingClientRect()
        // A zero-size rect is a panel that is present but not laid out; treating
        // it as a real one would blank a corner of the field for nothing.
        if (br.width <= 0 || br.height <= 0) continue
        blocks.push({
          l: br.left - hr.left,
          t: br.top - hr.top,
          r: br.right - hr.left,
          b: br.bottom - hr.top,
        })
      }
      const underChrome = (px: number, py: number, r: number) =>
        blocks.some((bk) => px + r > bk.l && px - r < bk.r && py + r > bk.t && py - r < bk.b)

      for (const pin of pins) {
        const el = refs.current.get(pin.id)
        if (!el) continue

        // The rendered position, from whichever camera just painted. `on` is
        // false off-stage or behind the camera in the spatial view, and a pin
        // for a node that is not on screen is a number pointing at nothing.
        const q = screen.place(pin.anchorId)
        if (!q.on) {
          el.style.visibility = 'hidden'
          continue
        }
        const x = q.x + pin.dx
        const y = q.y + pin.dy
        const r = pin.n === null ? DOT_R : PIN_R
        // Gate on the PIN's own box, unlike the caption layer's gate on the
        // node: a pin is 18px in one direction, so a node just inside the rim
        // would otherwise push its pin half off the edge.
        if (W > 0 && H > 0 && (x < 12 || x > W - 12 || y < 12 || y > H - 12)) {
          el.style.visibility = 'hidden'
          continue
        }
        // Under a column reserved at the right edge. The pin's own box again,
        // not its centre: a mark half-covered by a pane is half-pressable, and
        // which half depends on where the node happens to sit. Nothing reserves
        // this side since the reading pane moved left — App writes 0 — but the
        // arm reads the inset, not the layout, and deleting it would couple
        // this file to App's current geometry.
        if (W > 0 && insetRight > 0 && x + r > W - insetRight) {
          el.style.visibility = 'hidden'
          continue
        }
        // The same rule in the left gutter — the sidebar's column plus the
        // thread's, one inset by the time it arrives here. Mirrored rather than
        // folded into the arm above because they are two edges with two insets,
        // and a single expression over `W` would go wrong the moment one of
        // them is open and the other is not.
        if (insetLeft > 0 && x - r < insetLeft) {
          el.style.visibility = 'hidden'
          continue
        }
        // …and not underneath a panel. `visibility` rather than `opacity` on
        // purpose: it takes the button out of the tab order too, so what is
        // unreachable by mouse is not still reachable, and unexplainable, by
        // keyboard.
        if (underChrome(x, y, r)) {
          el.style.visibility = 'hidden'
          continue
        }
        el.style.visibility = 'visible'
        el.style.transform = `translate(-50%,-50%) translate(${x.toFixed(1)}px,${y.toFixed(1)}px)`
      }
    }

    // Driven by the canvas's loop, not one of our own — a pin has to be placed
    // with the very camera the strands were just painted with, or it slides off
    // its node whenever the view is moving.
    return onAfterDraw(tick)
  }, [show, pins])

  if (!show) return null

  return (
    <div className="cuepins" ref={hostRef} role="group" aria-label="Cues on the graph">
      {/* Real tab stops, unlike the captions below them, which are `aria-hidden`
          mouse affordances with the full node list offered separately. A pin is
          the only pointer-free way to jump STRAIGHT to cue five; J and K walk
          there one at a time. */}
      {pins.map((pin) =>
        pin.n === null ? (
          // Inert on purpose: a dot marks a node the cue's own sentence already
          // names, and making it focusable would put a second, wordless route to
          // the same cue in the tab order. So it is not a control and it is out
          // of the accessibility tree — but it does keep its pointer events,
          // because `title` is the only thing that says which node it is and a
          // browser shows no tooltip for something the pointer cannot reach. The
          // mouse gets the name here; everyone gets it from the card's sentence.
          <span
            key={pin.id}
            ref={(el) => {
              if (el) refs.current.set(pin.id, el)
              else refs.current.delete(pin.id)
            }}
            className="cuepin cuepin--also"
            aria-hidden
            title={pin.name ?? undefined}
            style={{ '--pin-tint': `var(--sem-${pin.channel})` } as CSSProperties}
          />
        ) : (
          <button
            key={pin.id}
            ref={(el) => {
              if (el) refs.current.set(pin.id, el)
              else refs.current.delete(pin.id)
            }}
            className="cuepin"
            data-current={pin.i === current || undefined}
            aria-label={`Cue ${pin.n}: ${CUE_LABEL[pin.kind]}`}
            title={CUE_LABEL[pin.kind]}
            // The disc is a MARK and the numeral on it is TEXT, so they take
            // different tiers of the same channel (D16): the ring and the fill
            // stay on `--sem-*`, where the canvas draws that channel, and the
            // digit reads through `--ink-*`. It matters most on the quiet
            // channels — a `--sem-dead` numeral at 10.5px on the panel ground
            // is very nearly not there.
            style={
              {
                '--pin-tint': `var(--sem-${pin.channel})`,
                '--pin-ink': `var(--ink-${pin.channel})`,
              } as CSSProperties
            }
            onClick={(e) => {
              // The canvas below reads clicks as select-or-deselect; a pin is
              // chrome sitting on top of it, not a gesture at the field.
              e.stopPropagation()
              // Through the store's own actions, both of them, in this order:
              // `openCoach` closes the digest and reopens the card, and the
              // index it lands on is the one this pin names.
              //
              // A third call stood here — `setRail(true)` — and it was not a
              // nicety: the card mounted inside the inspector and nowhere else,
              // and on a phone nothing else opened that pane, so a pin that only
              // moved the index lit a number and produced no card at all. The
              // card is a popup on the graph now, and at phone width it is a row
              // in the run-done column, so the press that raised a sheet over
              // the field is dead coupling and is gone (D25).
              openCoach()
              setCoachIndex(pin.i)
            }}
          >
            {pin.n}
          </button>
        ),
      )}
    </div>
  )
}
